import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

/**
 * CrowdSec on every node, what it blocks, and what it never blocks (Oz, 2026-10-08: "is the crowdsec
 * module complete — it shows the registration status, what's blocked, and if everything is ok?").
 *
 * Each node records its own row every gate pass, so this page shows all of them without asking any.
 * A null is NOT MEASURED — shown as unknown, never as off — and a row that has not been refreshed for
 * ten minutes says so instead of passing for current.
 */

type NodeRow = {
  node: string;
  engine_active: boolean | null;
  bouncer_active: boolean | null;
  bans_enforced: boolean | null;
  enforce_detail?: string;
  capi_connected: boolean | null;
  capi_refused: boolean | null;
  capi_detail?: string;
  pulls_community: boolean | null;
  blind_sources?: string[] | null;
  measured_at: string;
  stale: boolean;
};
type Blocked = {
  total: number;
  by_origin: Record<string, number> | null;
  by_reason: { reason: string; origin: string; count: number }[] | null;
  newest: { value: string; reason: string; origin: string; duration: string; at: string }[] | null;
  allowlist: { list: string; why: string; value: string; description?: string; expires?: string }[] | null;
  note?: string;
};

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : "—");
const ORIGIN: Record<string, string> = { crowdsec: "this cluster's own detections", CAPI: "community blocklist", cscli: "manual", lists: "subscribed lists" };

function tri(v: boolean | null, yes: string, no: string, tone: "bad" | "warn" = "bad") {
  if (v == null) return <StatusPill tone="neutral">unknown</StatusPill>;
  return v ? <StatusPill tone="ok">{yes}</StatusPill> : <StatusPill tone={tone}>{no}</StatusPill>;
}

export function CrowdSecOps() {
  return (
    <div className="space-y-4">
      <EveryNode />
      <WhatIsBlocked />
    </div>
  );
}

function EveryNode() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["crowdsec-cluster"],
    queryFn: () => client.apiFetch<{ nodes: NodeRow[] | null }>("/crowdsec/cluster"),
    refetchInterval: 30000,
  });
  const [confirm, setConfirm] = React.useState<string | null>(null);
  const reg = useMutation({
    mutationFn: (node: string) =>
      client.apiFetch<{ state?: string; detail?: string }>("/crowdsec/capi/register", { method: "POST", body: JSON.stringify({ node, force: true }) }),
    onSuccess: (r, node) => {
      notify({ kind: "success", title: `${node}: ${r?.detail ?? "done"}` });
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ["crowdsec-cluster"] });
    },
    onError: (e: unknown, node) => notify({ kind: "error", title: `${node}: not registered`, body: e instanceof Error ? e.message : String(e) }),
  });
  const nodes = q.data?.nodes ?? [];
  const bad = nodes.filter(
    (n) => !n.stale && (n.engine_active === false || n.bouncer_active === false || n.bans_enforced === false || n.capi_refused === true || (n.blind_sources ?? []).length > 0),
  ).length;

  const cols: DataListColumn<NodeRow>[] = [
    { key: "node", label: "Node", sortable: true, value: (n) => n.node, render: (n) => <span className="font-mono text-[12px]">{n.node}</span> },
    {
      key: "engine",
      label: "Engine · bouncer",
      render: (n) => (
        <div className="flex flex-wrap gap-1">
          {tri(n.engine_active, "engine", "engine down")}
          {tri(n.bouncer_active, "bouncer", "bouncer down")}
        </div>
      ),
    },
    {
      key: "bans_enforced",
      label: "Bans enforced",
      // The bouncer's whole job, measured by its effect: the newest ban is in this node's kernel set.
      render: (n) => (
        <div>
          {tri(n.bans_enforced, "in the kernel", "NOT enforced")}
          <div className="mt-0.5 text-[11px] text-muted">{n.enforce_detail}</div>
        </div>
      ),
    },
    {
      key: "capi",
      label: "Central API",
      render: (n) => (
        <div>
          <div className="flex flex-wrap gap-1">
            {n.capi_connected == null ? (
              <StatusPill tone="neutral">not asked</StatusPill>
            ) : n.capi_connected ? (
              <StatusPill tone="ok">accepted</StatusPill>
            ) : (
              <StatusPill tone="bad">{n.capi_refused ? "refused" : "not reached"}</StatusPill>
            )}
            {n.pulls_community && <StatusPill tone="neutral">pulls the community list</StatusPill>}
          </div>
          {n.capi_connected === false && <div className="mt-0.5 text-[11px] text-muted">{n.capi_detail}</div>}
        </div>
      ),
    },
    {
      key: "blind",
      label: "Log sources",
      render: (n) =>
        (n.blind_sources ?? []).length > 0 ? (
          <span className="text-[11.5px] text-crit">cannot see: {(n.blind_sources ?? []).join(", ")}</span>
        ) : (
          <span className="text-[11.5px] text-muted">all read</span>
        ),
    },
    {
      key: "measured_at",
      label: "Measured",
      value: (n) => n.measured_at,
      render: (n) => <span className={`text-[11.5px] ${n.stale ? "text-crit" : "text-muted"}`}>{n.stale ? "not reporting since " : ""}{when(n.measured_at)}</span>,
    },
  ];

  return (
    <Section title="Every node" hint={q.data ? (bad ? `${bad} node(s) need attention` : `${nodes.length} node(s), all well`) : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the CrowdSec node records">
          <DataList
            items={nodes}
            columns={cols}
            getRowKey={(n) => n.node}
            rowActions={(n: NodeRow) =>
              n.capi_refused ? (
                <Button size="sm" variant="danger" onClick={() => setConfirm(n.node)}>
                  Register again
                </Button>
              ) : null
            }
            emptyMessage="No node has recorded its CrowdSec state yet."
          />
        </StateView>
      </div>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Register ${confirm} with the central API again?`}
        confirmLabel="Register again"
        destructive
        pending={reg.isPending}
        body={
          <div className="space-y-2 text-[12.5px]">
            <p>
              This mints a NEW machine for {confirm}: its console enrollment, if it has one, belongs to the old identity and is
              lost. The engine restarts, and the central API is asked again to prove it.
            </p>
            <p className="text-muted">
              A refusal can be the central API's own and end by itself — on 2026-10-07/08 it refused valid machines for about 13
              hours, then accepted them again unchanged. If it began recently, waiting may be the better choice.
            </p>
          </div>
        }
        onConfirm={() => confirm && reg.mutate(confirm)}
      />
    </Section>
  );
}

function WhatIsBlocked() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["crowdsec-blocked"],
    queryFn: () => client.apiFetch<Blocked>("/crowdsec/blocked"),
    refetchInterval: 60000,
  });
  const d = q.data;
  const reasons = d?.by_reason ?? [];
  const newest = d?.newest ?? [];
  const allow = d?.allowlist ?? [];

  const banCols: DataListColumn<NonNullable<Blocked["newest"]>[number]>[] = [
    { key: "at", label: "Banned", sortable: true, value: (b) => b.at, render: (b) => <span className="text-[12px]">{when(b.at)}</span> },
    { key: "value", label: "Address", render: (b) => <span className="font-mono text-[12px]">{b.value}</span> },
    { key: "reason", label: "Why", sortable: true, value: (b) => b.reason },
    { key: "origin", label: "By", render: (b) => <span className="text-[12px]">{ORIGIN[b.origin] ?? b.origin}</span> },
    { key: "duration", label: "Left", render: (b) => <span className="text-[12px] text-muted">{b.duration}</span> },
  ];
  const allowCols: DataListColumn<NonNullable<Blocked["allowlist"]>[number]>[] = [
    { key: "value", label: "Address", render: (a) => <span className="font-mono text-[12px]">{a.value}</span> },
    { key: "list", label: "List", sortable: true, value: (a) => a.list },
    { key: "why", label: "Why it is never banned", render: (a) => <span className="text-[11.5px] leading-relaxed">{a.description ? `${a.description} — ` : ""}{a.why}</span> },
    { key: "expires", label: "Until", render: (a) => <span className="text-[12px] text-muted">{a.expires ? when(a.expires) : "permanent"}</span> },
  ];

  return (
    <>
      <Section title="What is blocked" hint={d ? `${d.total.toLocaleString()} address(es)` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <StateView loading={q.isLoading} error={q.error} errorContext="the ban list">
            <div className="mb-3 flex flex-wrap gap-x-8 gap-y-2">
              {Object.entries(d?.by_origin ?? {})
                .sort((a, b) => b[1] - a[1])
                .map(([o, n]) => (
                  <div key={o}>
                    <div className="text-[11px] uppercase tracking-wide text-muted">{ORIGIN[o] ?? o}</div>
                    <div className="font-mono text-[15px]">{n.toLocaleString()}</div>
                  </div>
                ))}
            </div>
            <div className="mb-4 space-y-1 text-[12.5px]">
              <div className="text-[11px] uppercase tracking-wide text-muted">Most bans by reason</div>
              {reasons.slice(0, 8).map((r) => (
                <div key={r.reason + r.origin} className="flex justify-between gap-4 border-b border-line py-1">
                  <span className="font-mono text-[12px]">{r.reason}</span>
                  <span className="text-muted">{ORIGIN[r.origin] ?? r.origin} · {r.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-muted">Newest bans by this cluster and by hand</div>
            <DataList items={newest} columns={banCols} getRowKey={(b) => b.value + b.at + b.reason} emptyMessage="No local or manual ban is active." />
            {d?.note && <p className="mt-2 text-[11.5px] text-muted">{d.note}</p>}
          </StateView>
        </div>
      </Section>
      <Section title="Never banned" hint={d ? `${allow.length} address(es)` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <StateView loading={q.isLoading} error={q.error} errorContext="the allowlists">
            <DataList items={allow} columns={allowCols} getRowKey={(a) => a.list + a.value} emptyMessage="No address is allowlisted." />
          </StateView>
        </div>
      </Section>
    </>
  );
}
